import {
  LEVEL_GROUP_METADATA,
  LEVEL_GROUP_ORDER,
  formatEducationLevel,
  getLevelGroupKey,
  normalizeEducationLevel,
} from '@tutor/types';
import { logger } from './logger';

export const LEVEL_GROUP_LABELS: Record<string, string> = {
  national_primary_lower: 'National · Standard 1-3',
  national_primary_upper: 'National · Standard 4-6',
  national_lower_secondary: 'National · Form 1-3',
  national_upper_secondary: 'National · Form 4-5',
  national_form_6: 'National · Form 6',
  cis_lower_secondary: 'CIS · Form 1-3',
  cis_upper_secondary: 'CIS · Form 4-6',
  international_primary: 'International · Year 1-6',
  international_lower_secondary: 'International · Year 7-9',
  international_upper_secondary: 'International · Year 10-11',
};

// Labels for the start boundary of each level group (used for span ranges)
export const LEVEL_GROUP_START_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(LEVEL_GROUP_METADATA).map(([key, metadata]) => [key, metadata.startLabel])
  ),
};

// Labels for the end boundary of each level group (used for span ranges)
export const LEVEL_GROUP_END_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(LEVEL_GROUP_METADATA).map(([key, metadata]) => [key, metadata.endLabel])
  ),
};

export const LEVEL_GROUP_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  national_primary_lower: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  national_primary_upper: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  national_lower_secondary: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  national_upper_secondary: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  national_form_6: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  cis_lower_secondary: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  cis_upper_secondary: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  international_primary: { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
  international_lower_secondary: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  international_upper_secondary: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
};

export type SubjectRangeGroup = {
  groupKey: string;
  label: string;
  subjects: string[];
};

export type SubjectAcrossRanges = {
  subject: string;
  ranges: string[];
  label: string;
  key: string; // unique key for React rendering
};

export type LevelRangeGroup = {
  rangeLabel: string; // e.g. "Standard 1-3 - Form 6" or "Form 6"
  subjects: string[]; // subjects taught across this full range
  key: string; // unique key for React rendering
};

// Short label per specific education level (for actual range display)
const EDUCATION_LEVEL_SHORT_LABELS: Record<string, string> = {
  national_standard_1: 'Standard 1',
  national_standard_2: 'Standard 2',
  national_standard_3: 'Standard 3',
  national_standard_4: 'Standard 4',
  national_standard_5: 'Standard 5',
  national_standard_6: 'Standard 6',
  national_form_1: 'Form 1',
  national_form_2: 'Form 2',
  national_form_3: 'Form 3',
  national_form_4: 'Form 4',
  national_form_5: 'Form 5',
  national_form_6: 'Form 6',
  cis_form_1: 'Form 1',
  cis_form_2: 'Form 2',
  cis_form_3: 'Form 3',
  cis_form_4: 'Form 4',
  cis_form_5: 'Form 5',
  cis_form_6: 'Form 6',
  international_year_1: 'Year 1',
  international_year_2: 'Year 2',
  international_year_3: 'Year 3',
  international_year_4: 'Year 4',
  international_year_5: 'Year 5',
  international_year_6: 'Year 6',
  international_year_7: 'Year 7',
  international_year_8: 'Year 8',
  international_year_9: 'Year 9',
  international_year_10: 'Year 10',
  international_year_11: 'Year 11',
};

// Global level order for sorting within/across groups
const LEVEL_ORDER = [
  'national_standard_1', 'national_standard_2', 'national_standard_3',
  'national_standard_4', 'national_standard_5', 'national_standard_6',
  'national_form_1', 'national_form_2', 'national_form_3',
  'national_form_4', 'national_form_5', 'national_form_6',
  'cis_form_1', 'cis_form_2', 'cis_form_3',
  'cis_form_4', 'cis_form_5', 'cis_form_6',
  'international_year_1', 'international_year_2', 'international_year_3',
  'international_year_4', 'international_year_5', 'international_year_6',
  'international_year_7', 'international_year_8', 'international_year_9',
  'international_year_10', 'international_year_11',
];

function buildActualRangeLabel(minLabel: string, maxLabel: string): string {
  if (minLabel === maxLabel) return minLabel;
  if (minLabel.startsWith('Standard') && maxLabel.startsWith('Standard'))
    return `Standard ${minLabel.split(' ')[1]}-${maxLabel.split(' ')[1]}`;
  if (minLabel.startsWith('Year') && maxLabel.startsWith('Year'))
    return `Year ${minLabel.split(' ')[1]}-${maxLabel.split(' ')[1]}`;
  if (minLabel.startsWith('Form') && maxLabel.startsWith('Form'))
    return `Form ${minLabel.split(' ')[1]}-${maxLabel.split(' ')[1]}`;
  return `${minLabel} - ${maxLabel}`;
}

export type ActualLevelRangeGroup = {
  groupKey: string;
  rangeLabel: string; // e.g. "Standard 2-3", "Form 4-5", "Form 6"
  subjects: string[];
};

/**
 * Groups subjects by their actual selected level range within each preset group.
 * E.g., if tutor selected Standard 2 and 3 (not all of Standard 1-3),
 * the label shows "Standard 2-3" not "Standard 1-3".
 */
export function groupSubjectsByActualRange(
  subjectCards: Array<{ educationLevel: string; subjects: string[] }>
): ActualLevelRangeGroup[] {
  const groupData: Record<string, { levels: Set<string>; subjects: Set<string> }> = {};

  for (const card of subjectCards) {
    const normalizedLevel = normalizeEducationLevel(card.educationLevel);
    const groupKey = getLevelGroupKey(normalizedLevel);
    if (!groupKey) continue;
    if (!groupData[groupKey]) groupData[groupKey] = { levels: new Set(), subjects: new Set() };
    groupData[groupKey].levels.add(normalizedLevel);
    card.subjects.forEach((s) => groupData[groupKey].subjects.add(s));
  }

  const result: ActualLevelRangeGroup[] = [];

  for (const [groupKey, { levels, subjects }] of Object.entries(groupData)) {
    if (subjects.size === 0) continue;
    const sortedLevels = Array.from(levels).sort(
      (a, b) => LEVEL_ORDER.indexOf(a) - LEVEL_ORDER.indexOf(b)
    );
    const minLabel = EDUCATION_LEVEL_SHORT_LABELS[sortedLevels[0]];
    const maxLabel = EDUCATION_LEVEL_SHORT_LABELS[sortedLevels[sortedLevels.length - 1]];
    const systemPrefix = formatEducationLevel(sortedLevels[0], {
      includeSystem: true,
      useShortSystemLabel: true,
    }).split(' · ')[0];
    result.push({
      groupKey,
      rangeLabel: `${systemPrefix} · ${buildActualRangeLabel(minLabel, maxLabel)}`,
      subjects: Array.from(subjects),
    });
  }

  return result.sort(
    (a, b) => LEVEL_GROUP_ORDER.indexOf(a.groupKey) - LEVEL_GROUP_ORDER.indexOf(b.groupKey)
  );
}

export function groupSubjectsByRange(
  subjectCards: Array<{ educationLevel: string; subjects: string[] }>
): SubjectRangeGroup[] {
  const groupMap: Record<string, Set<string>> = {};
  for (const card of subjectCards) {
    const groupKey = getLevelGroupKey(card.educationLevel);
    if (!groupKey) continue;
    if (!groupMap[groupKey]) groupMap[groupKey] = new Set();
    card.subjects.forEach((s) => groupMap[groupKey].add(s));
  }
  return Object.keys(LEVEL_GROUP_LABELS)
    .filter((key) => groupMap[key]?.size)
    .map((key) => ({
      groupKey: key,
      label: LEVEL_GROUP_LABELS[key],
      subjects: [...groupMap[key]],
    }));
}

/**
 * Group subjects by level range, showing all subjects taught across each contiguous span.
 * E.g., English & Mathematics at all levels, Further Mathematics only at Form 6:
 *   - { rangeLabel: "Standard 1-3 - Form 6", subjects: ["English", "Mathematics"] }
 *   - { rangeLabel: "Form 6", subjects: ["Further Mathematics", "Matematik M", "Matematik T"] }
 */
export function groupSubjectsByLevelRange(
  subjectCards: Array<{ educationLevel: string; subjects: string[] }>
): LevelRangeGroup[] {
  // Build map: subject -> set of level group keys
  const subjectRangesMap: Record<string, Set<string>> = {};
  for (const card of subjectCards) {
    const groupKey = getLevelGroupKey(card.educationLevel);
    if (!groupKey) continue;
    for (const subject of card.subjects) {
      if (!subjectRangesMap[subject]) subjectRangesMap[subject] = new Set();
      subjectRangesMap[subject].add(groupKey);
    }
  }

  // Group subjects by their contiguous level span
  const spanGroups: Record<string, string[]> = {};

  for (const [subject, rangesSet] of Object.entries(subjectRangesMap)) {
    const allRanges = LEVEL_GROUP_ORDER.filter((key) => rangesSet.has(key));

    // Split into contiguous segments
    const segments: string[][] = [];
    let currentSegment: string[] = [];

    for (let i = 0; i < allRanges.length; i++) {
      if (currentSegment.length === 0) {
        currentSegment.push(allRanges[i]);
      } else {
        const prevIndex = LEVEL_GROUP_ORDER.indexOf(currentSegment[currentSegment.length - 1]);
        const currIndex = LEVEL_GROUP_ORDER.indexOf(allRanges[i]);
        if (currIndex === prevIndex + 1) {
          currentSegment.push(allRanges[i]);
        } else {
          segments.push(currentSegment);
          currentSegment = [allRanges[i]];
        }
      }
    }
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }

    // Add subject to each span group
    for (const segment of segments) {
      const spanKey = segment.join('-');
      if (!spanGroups[spanKey]) spanGroups[spanKey] = [];
      spanGroups[spanKey].push(subject);
    }
  }

  // Build result
  const result: LevelRangeGroup[] = [];
  for (const [spanKey, subjects] of Object.entries(spanGroups)) {
    const segments = spanKey.split('-');
    const startLabel = LEVEL_GROUP_LABELS[segments[0]];
    const rangeLabel =
      segments.length === 1
        ? startLabel
        : `${LEVEL_GROUP_START_LABELS[segments[0]]} - ${LEVEL_GROUP_END_LABELS[segments[segments.length - 1]]}`;

    result.push({
      rangeLabel,
      subjects,
      key: spanKey,
    });
  }

  // Sort by first level group position
  result.sort((a, b) => {
    const aFirst = a.key.split('-')[0];
    const bFirst = b.key.split('-')[0];
    return LEVEL_GROUP_ORDER.indexOf(aFirst) - LEVEL_GROUP_ORDER.indexOf(bFirst);
  });

  return result;
}

/**
 * Group subjects by subject name, showing the range of levels they're taught across.
 * Non-contiguous level groups are split into separate entries.
 * E.g., English at Standard 1-3 and Form 6 (but not in between) becomes:
 *   - "English (Standard 1-3)"
 *   - "English (Form 6)"
 */
export function groupSubjectsBySubjectAcrossRanges(
  subjectCards: Array<{ educationLevel: string; subjects: string[] }>
): SubjectAcrossRanges[] {
  const subjectRangesMap: Record<string, Set<string>> = {};

  // Build a map of subject -> set of level groups
  for (const card of subjectCards) {
    const groupKey = getLevelGroupKey(card.educationLevel);
    if (!groupKey) continue;

    for (const subject of card.subjects) {
      if (!subjectRangesMap[subject]) subjectRangesMap[subject] = new Set();
      subjectRangesMap[subject].add(groupKey);
    }
  }

  const result: SubjectAcrossRanges[] = [];

  for (const [subject, rangesSet] of Object.entries(subjectRangesMap)) {
    // Get all level groups for this subject in order
    const allRanges = LEVEL_GROUP_ORDER.filter((key) => rangesSet.has(key));

    // Split into contiguous segments
    const segments: string[][] = [];
    let currentSegment: string[] = [];

    for (let i = 0; i < allRanges.length; i++) {
      if (currentSegment.length === 0) {
        currentSegment.push(allRanges[i]);
      } else {
        const prevIndex = LEVEL_GROUP_ORDER.indexOf(currentSegment[currentSegment.length - 1]);
        const currIndex = LEVEL_GROUP_ORDER.indexOf(allRanges[i]);
        if (currIndex === prevIndex + 1) {
          currentSegment.push(allRanges[i]);
        } else {
          segments.push(currentSegment);
          currentSegment = [allRanges[i]];
        }
      }
    }
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }

    // Generate an entry for each contiguous segment
    for (const segment of segments) {
      const startLabel = LEVEL_GROUP_LABELS[segment[0]];
      const label =
        segment.length === 1
          ? `${subject} (${startLabel})`
          : `${subject} (${LEVEL_GROUP_START_LABELS[segment[0]]} to ${LEVEL_GROUP_END_LABELS[segment[segment.length - 1]]})`;

      result.push({
        subject,
        ranges: segment,
        label,
        key: `${subject}-${segment.join('-')}`,
      });
    }
  }

  // Sort by subject name, then by first range position
  const sorted = result.sort((a, b) => {
    const cmp = a.subject.localeCompare(b.subject);
    if (cmp !== 0) return cmp;
    return LEVEL_GROUP_ORDER.indexOf(a.ranges[0]) - LEVEL_GROUP_ORDER.indexOf(b.ranges[0]);
  });

  logger.debug('subjectUtils', 'groupSubjectsBySubjectAcrossRanges result:', sorted);

  return sorted;
}
